/**
 * @file: provider.tsx
 *
 * Owns the inbox context state — a small set of component-scoped rxjs
 * subjects that coordinate the inbox surface (active tab, the open thread,
 * the transient overlay, the room-info visibility + refetch nonce).
 */

import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { useCreation } from '@euler/utils/hooks';
import { FC, ReactNode, memo, useEffect, useMemo } from 'react';
import { BehaviorSubject } from 'rxjs';
import { InboxContext } from './context';
import {
  InboxContextProps,
  InboxLayoutMode,
  InboxOverlay,
  InboxTab,
  InboxThreadItem,
} from './types';

export const InboxProvider: FC<{
  initialTab?: InboxTab;
  children: ReactNode;
}> = memo(({ initialTab, children }) => {
  const isDesktop = useIsDesktopLayout();

  const layoutMode$ = useCreation(
    () =>
      new BehaviorSubject<InboxLayoutMode>(isDesktop ? 'desktop' : 'mobile'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  // initialTab seeds the subject once; later changes don't recreate it.
  const activeTab$ = useCreation(
    () => new BehaviorSubject<InboxTab>(initialTab ?? 'notifications'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const activeThread$ = useCreation(
    () => new BehaviorSubject<InboxThreadItem | null>(null),
    [],
  );
  const overlay$ = useCreation(
    () => new BehaviorSubject<InboxOverlay | null>(null),
    [],
  );
  const infoOpen$ = useCreation(() => new BehaviorSubject(false), []);
  const nonce$ = useCreation(() => new BehaviorSubject(0), []);
  const feedListReloadTick$ = useCreation(() => new BehaviorSubject(0), []);

  // Keep the layout mode in sync as the viewport crosses the breakpoint.
  useEffect(() => {
    layoutMode$.next(isDesktop ? 'desktop' : 'mobile');
  }, [isDesktop, layoutMode$]);

  const value: InboxContextProps = useMemo(
    () => ({
      layoutMode$,
      activeTab$,
      activeThread$,
      overlay$,
      infoOpen$,
      nonce$,
      feedListReloadTick$,
    }),
    [
      layoutMode$,
      activeTab$,
      activeThread$,
      overlay$,
      infoOpen$,
      nonce$,
      feedListReloadTick$,
    ],
  );

  return (
    <InboxContext.Provider value={value}>{children}</InboxContext.Provider>
  );
});
